
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing again
    if (error) setError(null)
  }

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const isFormValid = () => {
    return formData.name && formData.email && validateEmail(formData.email) && formData.message
  }

  // Add keyboard event handler for Enter key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only proceed if the key is Enter, form is valid, and not currently submitting
      if (e.key === 'Enter' && isFormValid() && !isSubmitting && e.target instanceof HTMLElement) {
        // Don't trigger if user is in the middle of typing in textarea (allow multiline)
        if (e.target.tagName === 'TEXTAREA' && (e.target as HTMLTextAreaElement).value !== formData.message) {
          return
        }
        e.preventDefault()
        handleSubmit(e as unknown as React.FormEvent)
      }
    }

    // Add event listener
    window.addEventListener('keydown', handleKeyDown)
    
    // Clean up event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [formData, isSubmitting])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Form validation
    if (!formData.name) {
      setError("Please enter your name.")
      return
    }
    
    if (!formData.email) {
      setError("Please enter your email address.")
      return
    }
    
    if (!validateEmail(formData.email)) {
      setError("Please enter a valid email address.")
      return
    }
    
    if (!formData.message) {
      setError("Please enter your message.")
      return
    }
    
    setIsSubmitting(true)
    setError(null)
    
    try {
      console.log("Sending contact form data:", formData);
      
      // Try send-contact-email function first
      const { data, error: fnError } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      })
      
      console.log('Contact form API response:', data);
      
      if (fnError) {
        console.error('Error from email service:', fnError);
        throw new Error(fnError.message || 'Failed to send message');
      }
      
      toast({
        title: "Message Sent",
        description: "Thank you for contacting us. We'll get back to you soon."
      })
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        message: ""
      })
    } catch (error: any) {
      console.error('Error sending message:', error)
      setError(error.message || "There was an error sending your message. Please try again.")
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was an error sending your message. Please try again."
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div>
        <label htmlFor="name" className="block text-sm font-medium mb-1 text-left text-[#18375d]">
          Name <span className="text-[#ea384c]">*</span>
        </label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Your Name"
          disabled={isSubmitting}
        />
      </div>
      
      <div>
        <label htmlFor="email" className="block text-sm font-medium mb-1 text-left text-[#18375d]">
          Email <span className="text-[#ea384c]">*</span>
        </label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Your Email"
          disabled={isSubmitting}
        />
      </div>
      
      <div>
        <label htmlFor="message" className="block text-sm font-medium mb-1 text-left text-[#18375d]">
          Message <span className="text-[#ea384c]">*</span>
        </label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Your Message"
          rows={5}
          disabled={isSubmitting}
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-[#29b554] hover:bg-[#29b554]/90" 
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </Button>
    </form>
  )
}
